/**
 * ChatDialogHeader Component
 * Dialog header with avatar, title, and action buttons
 */

import React from "react";
import { Box, DialogTitle, Typography, Avatar, IconButton, Tooltip, useTheme, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import supportImage from "../../ressources/support.png";
import { formatBrandCopy } from "../../utils/brandCopy";

const ChatDialogHeader = ({
        isMobile,
        selectedChatId,
        disableInteractions,
        onClose,
        onDelete,
        onHistoryToggle,
        isFullscreen,
        onFullscreenToggle,
}) => {
        const { t } = useTranslation();
        const theme = useTheme();

        return (
                <DialogTitle
                        sx={{
                                padding: theme.spacing(isMobile ? 2 : 3, isMobile ? 2 : 3, isMobile ? 1.5 : 2),
                                fontSize: isMobile ? "1.25rem" : "1.5rem",
                                fontWeight: 600,
                                letterSpacing: "-0.01em",
                                color: theme.palette.text.primary,
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                background: `linear-gradient(135deg, ${alpha(
                                        theme.palette.primary.main,
                                        0.05
                                )} 0%, transparent 100%)`,
                                display: "flex",
                                flexWrap: isMobile ? "wrap" : "nowrap",
                                alignItems: isMobile ? "flex-start" : "center",
                                gap: 1.5,
                                mb: 0,
                        }}
                >
                        <Avatar
                                src={supportImage}
                                alt="Lisa"
                                sx={{
                                        borderRadius: "8px",
                                        width: isMobile ? 40 : 48,
                                        height: isMobile ? 40 : 48,
                                        objectFit: "contain",
                                }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                        variant="h6"
                                        component="div"
                                        fontWeight="600"
                                        fontSize={isMobile ? "1.25rem" : "1.5rem"}
                                        sx={{ letterSpacing: "-0.01em" }}
                                >
                                        {t("ai-chat-assistant") || "AI Assistant"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                        {formatBrandCopy(t("ai-chat-subtitle", { defaultValue: "Farm Management Assistant" }))}
                                </Typography>
                        </Box>
                        <Box
                                sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                }}
                        >
                                {isMobile && (
                                        <Tooltip title={t("ai-chat-history-toggle-open")} arrow>
                                                <span>
                                                        <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={onHistoryToggle}
                                                        >
                                                                <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                </span>
                                        </Tooltip>
                                )}
                                {!isMobile && (
                                        <Tooltip title={isFullscreen ? t("ai-chat-fullscreen-exit") || "Exit fullscreen" : t("ai-chat-fullscreen-enter") || "Enter fullscreen"} arrow>
                                                <span>
                                                        <IconButton
                                                                size="small"
                                                                onClick={onFullscreenToggle}
                                                                disabled={disableInteractions}
                                                        >
                                                                {isFullscreen ? (
                                                                        <FullscreenExitIcon fontSize="small" />
                                                                ) : (
                                                                        <FullscreenIcon fontSize="small" />
                                                                )}
                                                        </IconButton>
                                                </span>
                                        </Tooltip>
                                )}
                                {selectedChatId && (
                                        <Tooltip title={t("ai-chat-history-delete-chat")} arrow>
                                                <span>
                                                        <IconButton
                                                                edge="end"
                                                                aria-label="delete"
                                                                onClick={() => onDelete(selectedChatId)}
                                                                size="small"
                                                                disabled={disableInteractions}
                                                        >
                                                                <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                </span>
                                        </Tooltip>
                                )}
                                <Tooltip title={t("ai-chat-close") || "Close"} arrow>
                                        <IconButton size="small" onClick={onClose}>
                                                <CloseIcon fontSize="small" />
                                        </IconButton>
                                </Tooltip>
                        </Box>
                </DialogTitle>
        );
};

export default ChatDialogHeader;

